import { ApolloError } from "@apollo/client";
import { Button, DialogActions } from "@mui/material";
import { Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { NotLicensed } from "features/feature-flags";
import { readUnlicensedFeature } from "utils/graphql";
import Card from "./Card";
import { copyErrors, createTraceableErrors } from "./errorUtils";
import TraceableErrorDetail from "./TraceableErrorDetail";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    traceableError: {
      padding: theme.spacing(0, 1, 1, 1),
      "&>:nth-child(odd)>div>div": {
        backgroundColor: "rgba(0, 0, 0, 0.04)"
      },
      "&>:nth-child(even)>div>div": {
        backgroundColor: "#ffffff"
      }
    }
  })
);

interface ErrorBoxProps {
  onClose?: (value: boolean) => void;
  apolloError: ApolloError | ApolloError[];
  title?: string;
  closable?: boolean;
  maxHeight?: number;
}
export default function ErrorBox({
  onClose,
  apolloError,
  title = "An error occurred",
  closable = false,
  maxHeight
}: ErrorBoxProps) {
  const classNames = useStyles({});
  const errors = Array.isArray(apolloError) ? apolloError : [apolloError];
  const unlicensedFeatures = new Set<string>();
  const remainingErrors = errors.flatMap(error => {
    const graphQLErrors = error.graphQLErrors.filter(graphQLError => {
      const feature = readUnlicensedFeature(graphQLError.extensions);
      if (!feature) return true;
      unlicensedFeatures.add(feature);
      return false;
    });

    if (graphQLErrors.length === error.graphQLErrors.length) return [error];
    if (
      graphQLErrors.length ||
      error.networkError ||
      error.clientErrors.length ||
      error.protocolErrors.length
    ) {
      return [new ApolloError({ ...error, graphQLErrors })];
    }
    return [];
  });
  const traceableErrors = createTraceableErrors(remainingErrors);
  const showErrorCard =
    remainingErrors.length > 0 || unlicensedFeatures.size === 0;
  return (
    <>
      {Array.from(unlicensedFeatures, feature => (
        <NotLicensed key={feature} feature={feature} />
      ))}
      {showErrorCard ? (
        <Card
          title={title}
          closable={closable}
          onClose={onClose}
          copy={() => {
            copyErrors(traceableErrors);
          }}
        >
          <div className={classNames.traceableError}>
            {traceableErrors.map(({ errorStacks }, index) => (
              <TraceableErrorDetail
                key={index}
                errorStacks={errorStacks}
                maxHeight={maxHeight}
              />
            ))}
          </div>
        </Card>
      ) : closable ? (
        <DialogActions>
          <Button
            variant="outlined"
            data-testid="close-errors"
            onClick={() => onClose?.(false)}
          >
            CLOSE
          </Button>
        </DialogActions>
      ) : null}
    </>
  );
}
